import type { LitterRead } from '@pedigreesmart/api-client'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { useSession, useTerm } from '../session/SessionProvider.js'
import './animals.css'

const PER_PAGINA = 25

/**
 * Le cucciolate — o i parti, secondo la specie.
 *
 * È l'unica schermata che mostra il registro **per evento riproduttivo**
 * invece che per soggetto: la domanda a cui risponde è «cosa è nato da questo
 * accoppiamento», e da un elenco di soggetti non si ricava senza incrociare
 * genitori e date a mano.
 *
 * **Nati e iscritti sono due numeri diversi, e si mostrano tutti e due.** Alla
 * nascita si dichiara quanti sono; iscritti sono quelli che poi hanno avuto un
 * numero di registro. La differenza è normale — cuccioli morti nei primi
 * giorni, cessioni senza iscrizione — e mostrarne uno solo farebbe sembrare un
 * errore quello che non lo è.
 *
 * La riga si apre sull'elenco dei nati iscritti, che è la ragione per cui si
 * guarda una cucciolata: quel dato non sta nella risorsa, si chiede filtrando
 * i soggetti per `litter.id`.
 */
export function LitterList({ onApriSoggetto }: { onApriSoggetto?: ((id: number) => void) | undefined }) {
  const { resources, species } = useSession()
  const [pagina, setPagina] = useState(1)
  const [aperta, setAperta] = useState<number | null>(null)
  const cucciolate = useTerm('litter')

  const query = useQuery({
    queryKey: ['litters', species?.code, pagina],
    queryFn: ({ signal }) =>
      resources.litters.list(
        species
          ? { 'species.code': species.code, page: pagina, items_per_page: PER_PAGINA }
          : { page: pagina, items_per_page: PER_PAGINA },
        signal,
      ),
    placeholderData: keepPreviousData,
  })

  const pagine = query.data ? Math.max(1, Math.ceil(query.data.totalItems / PER_PAGINA)) : 1

  return (
    <section aria-labelledby="titolo-cucciolate">
      <div className="elenco__testa">
        <h1 id="titolo-cucciolate">{cucciolate}</h1>
        {query.data && (
          <p className="elenco__conteggio" aria-live="polite">
            {query.data.totalItems === 1 ? '1 evento' : `${query.data.totalItems} eventi`}
          </p>
        )}
      </div>

      {query.isError && (
        <p role="alert" className="messaggio messaggio--errore">
          Non è stato possibile leggere l'elenco.
        </p>
      )}

      {query.isPending && <p className="messaggio">Caricamento…</p>}

      {query.data?.member.length === 0 && (
        <p className="messaggio">Nessun evento riproduttivo registrato per questa specie.</p>
      )}

      {query.data && query.data.member.length > 0 && (
        <ul className="cucciolate" data-attesa={query.isFetching ? 'si' : undefined}>
          {query.data.member.map((cucciolata) => (
            <Riga
              key={cucciolata.id}
              cucciolata={cucciolata}
              aperta={aperta === cucciolata.id}
              onApri={() => {
                setAperta(aperta === cucciolata.id ? null : cucciolata.id)
              }}
              onApriSoggetto={onApriSoggetto}
            />
          ))}
        </ul>
      )}

      {pagine > 1 && (
        <nav className="paginazione" aria-label="Pagine">
          <button
            type="button"
            data-variante="secondario"
            disabled={pagina <= 1}
            onClick={() => {
              setPagina((p) => p - 1)
            }}
          >
            Precedente
          </button>
          <span aria-live="polite">
            Pagina {pagina} di {pagine}
          </span>
          <button
            type="button"
            data-variante="secondario"
            disabled={pagina >= pagine}
            onClick={() => {
              setPagina((p) => p + 1)
            }}
          >
            Successiva
          </button>
        </nav>
      )}
    </section>
  )
}

function Riga({
  cucciolata,
  aperta,
  onApri,
  onApriSoggetto,
}: {
  cucciolata: LitterRead
  aperta: boolean
  onApri: () => void
  onApriSoggetto?: ((id: number) => void) | undefined
}) {
  const madre = useTerm('dam')
  const padre = useTerm('sire')

  return (
    <li className="cucciolata">
      <button
        type="button"
        className="cucciolata__testa"
        data-variante="piatto"
        aria-expanded={aperta}
        onClick={onApri}
      >
        <span className="riga__nome">
          {cucciolata.dam?.name ?? '—'} × {cucciolata.sire?.name ?? '—'}
        </span>
        <span className="storico__periodo">{data(cucciolata.birth_date) ?? '—'}</span>
        <span className="riga__secondario">
          {/* I due numeri insieme, e con le parole della specie: «3 iscritti
              su 5 nati» si legge, «3/5» va interpretato. */}
          {conteggio(cucciolata)}
        </span>
      </button>

      <p className="riga__secondario cucciolata__genitori">
        {madre}: {cucciolata.dam?.name ?? '—'} · {padre}: {cucciolata.sire?.name ?? '—'}
        {cucciolata.breeder?.display_name && ` · allevatore: ${cucciolata.breeder.display_name}`}
      </p>

      {aperta && <Nati litterId={cucciolata.id} onApriSoggetto={onApriSoggetto} />}
    </li>
  )
}

/**
 * I soggetti nati da quella cucciolata.
 *
 * Si chiedono solo quando la riga si apre: caricarli tutti insieme
 * significherebbe una richiesta per riga su una pagina che ne ha venticinque,
 * per un dato che quasi sempre non si guarda.
 */
function Nati({
  litterId,
  onApriSoggetto,
}: {
  litterId: number
  onApriSoggetto?: ((id: number) => void) | undefined
}) {
  const { resources } = useSession()

  const nati = useQuery({
    queryKey: ['animals', 'litter', litterId],
    queryFn: ({ signal }) =>
      resources.animals.list({ 'litter.id': litterId, items_per_page: 50 }, signal),
  })

  if (nati.isPending) return <p className="messaggio">Caricamento…</p>

  if (nati.isError) {
    return (
      <p role="alert" className="messaggio messaggio--errore">
        Non è stato possibile leggere i nati.
      </p>
    )
  }

  if (nati.data.member.length === 0) {
    return <p className="messaggio">Nessun nato iscritto al registro.</p>
  }

  return (
    <ul className="cucciolata__nati">
      {nati.data.member.map((nato) => (
        <li key={nato.id}>
          <button
            type="button"
            data-variante="piatto"
            disabled={!onApriSoggetto}
            onClick={() => {
              onApriSoggetto?.(nato.id)
            }}
          >
            {nato.full_name ?? nato.name}
            {nato.registry_number && <span className="codice"> {nato.registry_number}</span>}
          </button>
        </li>
      ))}
    </ul>
  )
}

function conteggio(cucciolata: LitterRead): string {
  const iscritti = cucciolata.registered_count ?? 0
  const nati = cucciolata.born_total

  if (typeof nati === 'number' && nati > 0) {
    return `${String(iscritti)} iscritti su ${String(nati)} nati`
  }

  return iscritti === 1 ? '1 iscritto' : `${String(iscritti)} iscritti`
}

function data(iso?: string | null): string | null {
  if (!iso) return null

  const quando = new Date(iso)

  return Number.isNaN(quando.getTime()) ? null : quando.toLocaleDateString('it-IT')
}
